import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, PRIORITY, TASK_STATUS } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const EMPTY = {
  title: "", description: "", assigned_to: "none", status: "todo",
  priority: "normal", due_date: "", estimated_hours: "", actual_hours: "",
};

export function TaskForm({ open, onOpenChange, task, projectId, projects = [], users = [], onSaved }) {
  const isEdit = !!task;
  const [form, setForm] = useState(EMPTY);
  const [pid, setPid] = useState(projectId || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPid(projectId || task?.project_id || "");
    if (task) {
      setForm({
        title: task.title, description: task.description || "",
        assigned_to: task.assigned_to || "none", status: task.status,
        priority: task.priority, due_date: task.due_date || "",
        estimated_hours: task.estimated_hours || "", actual_hours: task.actual_hours || "",
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, task, projectId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Titel ist erforderlich");
    const targetPid = isEdit ? task.project_id : pid;
    if (!targetPid) return toast.error("Bitte ein Projekt auswählen");
    setSaving(true);
    const payload = {
      ...form,
      assigned_to: form.assigned_to === "none" ? null : form.assigned_to,
      due_date: form.due_date || null,
      estimated_hours: parseFloat(form.estimated_hours) || 0,
      actual_hours: parseFloat(form.actual_hours) || 0,
    };
    try {
      const res = isEdit
        ? await api.put(`/tasks/${task.id}`, payload)
        : await api.post(`/projects/${targetPid}/tasks`, payload);
      toast.success(isEdit ? "Aufgabe aktualisiert" : "Aufgabe erstellt");
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-2xl" data-testid="task-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#1D225C]">
            {isEdit ? "Aufgabe bearbeiten" : "Neue Aufgabe"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {!isEdit && !projectId && (
            <div className="sm:col-span-2">
              <Label>Projekt *</Label>
              <Select value={pid} onValueChange={setPid}>
                <SelectTrigger data-testid="task-form-project" className="mt-1.5"><SelectValue placeholder="Projekt wählen" /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.client_name} – {p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="sm:col-span-2">
            <Label>Titel *</Label>
            <Input data-testid="task-form-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="z. B. Startseite umsetzen" className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label>Beschreibung</Label>
            <Textarea data-testid="task-form-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className="mt-1.5" />
          </div>
          <div>
            <Label>Zugewiesen an</Label>
            <Select value={form.assigned_to} onValueChange={(v) => set("assigned_to", v)}>
              <SelectTrigger data-testid="task-form-assignee" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Niemand</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="task-form-status" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TASK_STATUS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Priorität</Label>
            <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger data-testid="task-form-priority" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(PRIORITY).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Fällig am</Label>
            <Input data-testid="task-form-due" type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Geschätzt (h)</Label>
            <Input data-testid="task-form-estimated" type="number" min="0" step="0.5" value={form.estimated_hours} onChange={(e) => set("estimated_hours", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Tatsächlich (h)</Label>
            <Input data-testid="task-form-actual" type="number" min="0" step="0.5" value={form.actual_hours} onChange={(e) => set("actual_hours", e.target.value)} className="mt-1.5" />
          </div>
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <Button variant="outline" data-testid="task-form-cancel" onClick={() => onOpenChange(false)} className="rounded-xl">Abbrechen</Button>
          <Button data-testid="task-form-submit" onClick={submit} disabled={saving} className="rounded-xl bg-[#1D225C] text-white hover:bg-[#2a3070]">
            {saving ? "Speichern…" : isEdit ? "Speichern" : "Aufgabe anlegen"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
